// Route Code Edited 08/10/2026
/**
 * Route Dialog Handler — Shopify Horizon (<theme-drawer>)
 *
 * PROBLEM
 * Horizon's cart drawer (assets/theme-drawer.js) opens its <dialog> panel with
 * showModal() below 990px:
 *
 *     const MODAL_BREAKPOINT = 990;
 *     #modalQuery = window.matchMedia(`(max-width: ${MODAL_BREAKPOINT - 1}px)`)
 *     this.#modalQuery.matches
 *       ? (lockScroll(panel), panel.showModal())   // <990px  -> top layer
 *       : (panel.show(), trapFocus(panel))         // >=990px -> normal flow
 *
 * showModal() promotes the panel to the browser's top layer, which paints above all
 * z-indexed content in the normal flow regardless of z-index value. Route's modal
 * (#rm-modal-container / .route-modal) is a body-level element positioned by z-index,
 * so below 990px it renders behind the open drawer. At >=990px Horizon already uses
 * show(), which is why the bug has a hard 990px boundary.
 *
 * FIX
 * Patch HTMLDialogElement.prototype.showModal so the cart drawer's panel opens with
 * show() instead, and replace the positioning and ::backdrop that showModal() would
 * otherwise have provided.
 *
 * WHY A PROTOTYPE PATCH AND NOT A CALL-SITE EDIT
 * assets/theme-drawer.js calls panel.showModal() from four places — open(),
 * #onRestore(), #onModalBreakpointChange(), and #bringToFront(). Three are ES private
 * methods inside a minified class body. Patching the DOM method they all call covers
 * every path with one change and leaves every merchant theme file untouched.
 *
 * SCOPE
 * Only dialogs inside theme-drawer#cart-drawer are affected. Every other <dialog> on
 * the site — including Horizon's other drawers — keeps native showModal() behavior.
 * Those still enter the top layer, so a menu or search drawer opened over the cart
 * drawer continues to paint above it regardless of the z-index set here.
 * Any unexpected error falls through to native showModal(), so a failure in this file
 * degrades to the theme's original behavior rather than a broken drawer.
 *
 * INSTALL
 * Upload as assets/route-dialog-handler.js, then add to layout/theme.liquid inside
 * <head>, BEFORE any of Horizon's <script type="module"> tags:
 *
 *     <!-- Route Code Edited 08/04/2026 -->
 *     <script src="{{ 'route-dialog-handler.js' | asset_url }}"></script>
 *     <!-- End Route Code -->
 *
 * Note the deliberate absence of `defer`. Deferred classic scripts and module scripts
 * both execute after parsing, in document order — so a deferred tag can still lose the
 * race to a module script declared earlier in the document. #onRestore() calls
 * showModal() when the element upgrades with `open` already set (section re-render,
 * Theme Editor), which can happen during Horizon's module evaluation. Loading this
 * synchronously in <head> guarantees the patch is installed first. The cost is one
 * small blocking request from the Shopify CDN.
 *
 * REMOVE
 * Delete the <script> tag above and delete assets/route-dialog-handler.js. No other
 * theme file is modified, so removing those two things is a complete rollback.
 *
 * NOT INCLUDED
 * showModal() supplies a focus trap, Esc-to-close, and background inertness. Horizon
 * only calls its own trapFocus() on the >=990px branch, so forcing show() leaves the
 * <990px drawer with neither.
 */
(function () {
  'use strict';

  var DRAWER_SELECTOR = 'theme-drawer#cart-drawer';
  var STYLE_ID = 'route-non-modal-cart-drawer-styles';
  var BACKDROP_CLASS = 'route-cart-backdrop';

  // Guard against this file being loaded twice (duplicate script tag, app embed
  // collision). Patching a patch would leave showModal wrapped twice.
  if (window.__routeDialogHandler && window.__routeDialogHandler.patched) {
    return;
  }

  var nativeShowModal = HTMLDialogElement.prototype.showModal;

  // Inspection hook. In the console: window.__routeDialogHandler
  // `intercepts` staying at 0 after opening the drawer below 990px means the gate
  // never matched — check that the drawer is still theme-drawer#cart-drawer before
  // assuming the fix is applied. This is the failure mode of the previous version of
  // this script, which silently no-opped on a stale class name.
  window.__routeDialogHandler = {
    patched: true,
    version: '2026-08-04',
    intercepts: 0,
    nativeShowModal: nativeShowModal
  };

  function injectStyles() {
    if (document.getElementById(STYLE_ID)) return;

    var style = document.createElement('style');
    style.id = STYLE_ID;
    style.textContent = [
      // Stacking base for the de-modaled drawer.
      //
      // In the top layer the drawer outranked everything on the page automatically.
      // Out of it, the drawer competes on z-index in the normal flow and loses to
      // Horizon's header (.header__row--top), so it needs a base high enough to clear
      // any theme value. Verified on this store: the drawer and the header row share a
      // stacking context, so raising the drawer alone is sufficient — no containment or
      // isolation override on the header is required.
      //
      // The value must stay BELOW Route's modal, which sits at the int32 max (2147483647).
      // A value of 2147480000 clears anything a Shopify theme will realistically set while 
      // leaving ~3600 of headroom under Route's modal.
      //
      // --drawer-stack-order is ThemeDrawer's static open counter, set on the host
      // element. Doubling it keeps consecutive drawers two apart so the dialog can sit
      // exactly one above its own backdrop. Headroom is exhausted only past ~1800
      // drawer opens in a single page session, where the failure is cosmetic.
      'theme-drawer#cart-drawer {',
      '  --route-drawer-z: calc(2147480000 + (var(--drawer-stack-order, 1) * 2));',
      '}',
      // showModal() centers the dialog in the viewport via UA styles that only apply
      // in the top layer. Reset the offset Horizon's CSS assumes it away from.
      'theme-drawer#cart-drawer > dialog.theme-drawer__dialog[open] {',
      '  top: 0 !important;',
      '  z-index: calc(var(--route-drawer-z) + 1);',
      '}',
      // Synthetic replacement for ::backdrop, which does not render outside the top
      // layer. The backdrop must stay inside the theme-drawer host to inherit
      // --route-drawer-z (and the --drawer-stack-order it derives from).
      'theme-drawer#cart-drawer > div.' + BACKDROP_CLASS + ' {',
      '  position: fixed;',
      '  inset: 0;',
      '  z-index: var(--route-drawer-z);',
      '  pointer-events: none;',
      '  background: rgba(0, 0, 0, 0.375);',
      '  opacity: 0;',
      '  transition: none !important;',
      '}',
      // ThemeDrawer.open() sets `open` on the host element, so the host attribute is a
      // cheaper and more stable hook than :has(dialog[open]).
      'theme-drawer#cart-drawer[open] > div.' + BACKDROP_CLASS + ' {',
      '  opacity: 1;',
      '  pointer-events: auto;',
      '}',
      // Overrides any contain: layout/paint the theme sets on the cart drawer section,
      // which would establish a stacking context and a containing block for the
      // fixed-position drawer now that it is out of the top layer. `contain: style`
      // only scopes counters and quotes, so this is a no-op unless the theme actually
      // sets layout/paint containment here. Retained because the fix was verified with
      // it in place; it is the first thing to try removing if you want to trim the CSS,
      // and the drawer's position/stacking is the thing to re-check if you do.
      '#shopify-section-cart-drawer-section:has(theme-drawer#cart-drawer[open]) {',
      '  contain: style;',
      '}'
    ].join('\n');

    document.head.appendChild(style);
  }

  function ensureBackdrop(panel, drawer) {
    var parent = panel.parentNode;
    if (!parent) return;

    var backdrop = parent.querySelector(':scope > div.' + BACKDROP_CLASS);

    if (!backdrop) {
      backdrop = document.createElement('div');
      backdrop.className = BACKDROP_CLASS;

      // A native modal dialog's ::backdrop is outside the drawer, so Horizon's
      // isClickedOutside() treats clicks on it as outside clicks and closes the
      // drawer. Our backdrop has to live inside theme-drawer to inherit
      // --drawer-stack-order, which means Horizon will read those clicks as *inside*
      // the drawer. Close explicitly to preserve click-outside-to-dismiss.
      if (drawer && typeof drawer.close === 'function') {
        backdrop.addEventListener('click', function () {
          drawer.close();
        });
      }
    }

    // Keep the backdrop immediately before the panel so paint order is predictable
    // even if the theme re-renders the drawer's children.
    if (panel.previousElementSibling !== backdrop) {
      parent.insertBefore(backdrop, panel);
    }
  }

  HTMLDialogElement.prototype.showModal = function () {
    var drawer;

    // Gate on the custom element name plus the section-assigned id only. Deliberately
    // NOT gating on .theme-drawer__dialog: every extra condition is another thing that
    // can silently start returning false after a Horizon update, and a BEM class is
    // less stable than the element name and id.
    try {
      drawer = this.closest(DRAWER_SELECTOR);
    } catch (err) {
      drawer = null;
    }

    if (!drawer) {
      return nativeShowModal.call(this);
    }

    try {
      injectStyles();
      ensureBackdrop(this, drawer);
      window.__routeDialogHandler.intercepts += 1;
      // Horizon calls lockScroll(panel) immediately before panel.showModal(), so page
      // scroll is already locked by the time we get here. No need to replicate it.
      return this.show();
    } catch (err) {
      // Anything unexpected: fall back to the theme's original behavior. The Route
      // modal will be obscured again, but the drawer itself keeps working.
      if (window.console && console.warn) {
        console.warn('[Route] Dialog handler failed; using native showModal().', err);
      }
      return nativeShowModal.call(this);
    }
  };
})();
// End Route Code